import axios from 'axios';

// REACT_APP_API_URL is baked in at build time (CRA), so it can't be set via
// docker-compose env_file at container runtime. Default to the API on the
// same host the page was loaded from (docker-compose publishes it on 8000
// alongside the frontend's own port) so a deploy to any host works without
// a rebuild; REACT_APP_API_URL still overrides it for local dev.
const API_BASE = process.env.REACT_APP_API_URL || (window.location.port === "3000" ? `${window.location.protocol}//${window.location.hostname}:8000/api` : "/api");

const client = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

export function skillPackageApi(scope = 'me') {
  const root = `/${scope}/skills`;
  return {
    list: () => client.get(root).then(r => r.data),
    create: (title, result_kind) => client.post(root, { title, result_kind: result_kind || null }).then(r => r.data),
    detail: id => client.get(`${root}/${id}`).then(r => r.data),
    edit: (id, revision, files) => client.put(`${root}/${id}/draft`, { revision, files }).then(r => r.data),
    read: (id, path, version_id) => client.get(`${root}/${id}/file`, { params: { path, version_id }, responseType: 'arraybuffer' }).then(r => r.data),
    validate: id => client.post(`${root}/${id}/validate`).then(r => r.data),
    publish: (id, revision, apply = false) => client.post(`${root}/${id}/publish`, { revision, apply }).then(r => r.data),
    authoring: id => client.get(`${root}/${id}/authoring`).then(r => r.data),
    sample: (id, revision, file) => client.post(`${root}/${id}/sample`, file, { params: { revision, fmt: file.name.split('.').pop().toLowerCase() }, headers: { 'Content-Type': 'application/octet-stream' } }).then(r => r.data),
    compose: (id, body) => client.post(`${root}/${id}/compose`, body).then(r => r.data),
    sampleRange: (id, body) => client.post(`${root}/${id}/sample-range`, body).then(r => r.data),
    saveAuthoring: (id, body) => client.put(`${root}/${id}/authoring`, body).then(r => r.data),
    preview: (id, revision) => client.post(`${root}/${id}/preview`, { revision }).then(r => r.data),
    previewFile: (id, path, original = false) => client.get(`${root}/${id}/preview-file`, { params: { path, original }, responseType: 'blob' }).then(r => r.data),
    import: (id, revision, file) => client.post(`${root}/${id}/import`, file, { params: { revision }, headers: { 'Content-Type': 'application/zip' } }).then(r => r.data),
    export: (id, version_id) => client.get(`${root}/${id}/export`, { params: { version_id }, responseType: 'blob' }).then(r => r.data),
    version: (id, versionId) => client.get(`${root}/${id}/versions/${versionId}`).then(r => r.data),
    archive: id => client.delete(`${root}/${id}`),
    purposes: () => client.get(scope === 'admin' ? '/admin/skill-purposes' : '/skill-purposes').then(r => r.data),
    savePurpose: (id, body) => client.put(`/admin/skill-purposes/${id}`, body).then(r => r.data),
    bindings: () => client.get(`/${scope}/skill-bindings`).then(r => r.data),
    bind: (id, mode, package_ids) => client.put(`/${scope}/skill-bindings/${id}`, { mode, package_ids }).then(r => r.data),
    unbind: id => client.delete(`/${scope}/skill-bindings/${id}`),
  };
}

// The browser sends the HttpOnly session cookie; mutating requests also carry
// the server-issued CSRF token from its readable companion cookie.
client.interceptors.request.use((config) => {
  const csrf = document.cookie.split('; ').find(part => part.startsWith('rnd_csrf='));
  if (csrf) config.headers['X-CSRF-Token'] = decodeURIComponent(csrf.slice('rnd_csrf='.length));
  return config;
});

// On 401 — clear session and redirect to /login
client.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      // Don't redirect for the login endpoint itself
      if (!url.endsWith('/users/login') && !url.endsWith('/users/me')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const api = {
  outputSourceRevision: (sessionId, id, revision) => client.get(`/sessions/${sessionId}/outputs/${id}/history/${revision}`).then(r => r.data),
  saveOutputMarkdown: (sessionId, id, revision, markdown) => client.put(`/sessions/${sessionId}/outputs/${id}/markdown`, { revision, markdown }).then(r => r.data),
  outputHistory: (sessionId, id) => client.get(`/sessions/${sessionId}/outputs/${id}/history`).then(r => r.data),
  restoreOutput: (sessionId, id, body) => client.post(`/sessions/${sessionId}/outputs/${id}/restore`, body).then(r => r.data),
  outputAssetUrl: (sessionId, id, name) => `${API_BASE}/sessions/${sessionId}/outputs/${id}/assets/${encodeURIComponent(name)}`,
  requestOutputExport: (sessionId, id, format, revision) => client.post(`/sessions/${sessionId}/outputs/${id}/exports`, { format, revision }).then(r => r.data),
  outputExportStatus: (sessionId, id, job) => client.get(`/sessions/${sessionId}/outputs/${id}/exports/${job}`).then(r => r.data),
  outputExportFile: (sessionId, id, job) => client.get(`/sessions/${sessionId}/outputs/${id}/exports/${job}/file`, { responseType: 'blob' }),
  getAttention: (params, options = {}) => client.get('/me/attention', { ...options, params }).then(r => r.data),
  getPrompts: () => client.get('/agents/prompts').then(r => r.data),
  getPromptVersions: (params = {}) => client.get('/agents/prompts/versions', { params }).then(r => r.data),
  getPromptVersion: revision => client.get(`/agents/prompts/versions/${revision}`).then(r => r.data),
  savePrompts: body => client.put('/agents/prompts', body).then(r => r.data),
  restorePrompts: body => client.post('/agents/prompts/restore', body).then(r => r.data),
  previewPrompts: body => client.post('/agents/prompts/preview', body).then(r => r.data),
  getLlmRequests: sessionId => client.get('/admin/llm-requests', { params: { session_id: sessionId } }).then(r => r.data),
  getLlmRequest: id => client.get(`/admin/llm-requests/${id}`).then(r => r.data),

  outputSettings: projectId => client.get(projectId ? `/projects/${projectId}/output-settings` : '/me/output-settings').then(r => r.data),
  saveOutputSettings: (projectId, outputs) => client.put(projectId ? `/projects/${projectId}/output-settings` : '/me/output-settings', { outputs }).then(r => r.data),
  sessionOutputs: sessionId => client.get(`/sessions/${sessionId}/outputs`).then(r => r.data),
  output: (sessionId, id) => client.get(`/sessions/${sessionId}/outputs/${id}`).then(r => r.data),
  retryOutput: (sessionId, id) => client.post(`/sessions/${sessionId}/outputs/${id}/retry`).then(r => r.data),
  regenerateOutput: (sessionId, id) => client.post(`/sessions/${sessionId}/outputs/${id}/regenerate`).then(r => r.data),
  reviewOutput: (sessionId, id) => client.post(`/sessions/${sessionId}/outputs/${id}/review`).then(r => r.data),
  editOutput: (sessionId, id, body) => client.patch(`/sessions/${sessionId}/outputs/${id}`, body).then(r => r.data),
  outputFile: (sessionId, id, path, revision) => client.get(`/sessions/${sessionId}/outputs/${id}/file`, { params: { path, revision }, responseType: 'blob' }),
  adminOverview: (collection, params) => client.get(`/admin/${collection}`, { params }).then(r => r.data),
  adminSession: id => client.get(`/admin/sessions/${id}`).then(r => r.data),
  adminTranscript: id => client.get(`/admin/sessions/${id}/transcript`).then(r => r.data),
  adminMaterials: (id, collection, params) => client.get(`/admin/sessions/${id}/materials/${collection}`, { params }).then(r => r.data),
  adminOriginal: (kind, id) => client.get(`/admin/moderation/originals/${kind}/${id}`).then(r => r.data),
  adminAttachmentDownload: id => client.get(`/admin/moderation/attachments/${id}/download`, { responseType: 'blob' }),
  moderationAction: data => client.post('/admin/moderation/actions', data).then(r => r.data),
  moderationActions: params => client.get('/admin/moderation/actions', { params }).then(r => r.data),
  moderationAssessments: params => client.get('/admin/moderation/assessments', { params }).then(r => r.data),
  moderationAssessment: id => client.get(`/admin/moderation/assessments/${id}`).then(r => r.data),
  moderationResolve: (id, data) => client.post(`/admin/moderation/assessments/${id}/resolve`, data).then(r => r.data),
  moderationRules: () => client.get('/admin/moderation/rules').then(r => r.data),
  saveModerationRule: data => client.post('/admin/moderation/rules', data).then(r => r.data),
  scanSession: (id, data) => client.post(`/admin/sessions/${id}/scan`, data).then(r => r.data),
  baseURL: API_BASE,
  uploadSessionAttachment: (projectId, file, uploadId, onUploadProgress) => {
    const form = new FormData();
    form.append('file', file);
    form.append('client_upload_id', uploadId);
    return client.post(`/projects/${projectId}/attachments`, form, {
      headers: { 'Content-Type': 'multipart/form-data' }, onUploadProgress,
    }).then(r => r.data);
  },
  getSessionAttachment: id => client.get(`/attachments/${id}`).then(r => r.data),
  listSessionAttachments: sessionId => client.get(`/sessions/${sessionId}/attachments`).then(r => r.data),
  readSessionAttachment: (id, offset = 0) => client.get(`/attachments/${id}/content`, { params: { offset } }).then(r => r.data),
  downloadSessionAttachment: id => client.get(`/attachments/${id}/download`, { responseType: 'blob' }),
  retrySessionAttachment: id => client.post(`/attachments/${id}/retry`).then(r => r.data),
  discardSessionAttachment: id => client.delete(`/attachments/${id}`),
  excludeSessionAttachment: (sessionId, id) => client.post(`/sessions/${sessionId}/attachments/${id}/exclude`).then(r => r.data),
  getLatestReportDocument: (sessionId) => client.get(`/sessions/${sessionId}/report-document`).then(r => r.data),
  getReportDocument: (id) => client.get(`/report-documents/${id}`).then(r => r.data),
  saveReportDocument: (id, body) => client.patch(`/report-documents/${id}`, body).then(r => r.data),
  stopReportDocument: (id) => client.post(`/report-documents/${id}/stop`).then(r => r.data),
  reportDocumentRevisions: (id) => client.get(`/report-documents/${id}/revisions`).then(r => r.data),
  exportReportDocument: (id, fmt, revision) => client.get(`/report-documents/${id}/export/${fmt}`, { params: { revision }, responseType: 'blob' }),
  setReviewLevel: (id, review_level) => client.patch(`/sessions/${id}/review-settings`, { review_level }).then(r => r.data),
  getCapabilities: () => client.get("/capabilities").then(r => r.data),
  reindexDocument: (projectId, docId) => client.post(`/projects/${projectId}/knowledge/${docId}/reindex`).then(r => r.data),
  // Auth / setup
  checkInitialized: () => client.get('/users/initialized').then(r => r.data),
  setupAdmin: (data) => client.post('/users/setup', data).then(r => r.data),
  loginUser: (email, password) => client.post('/users/login', { email, password }).then(r => r.data),
  getMe: () => client.get('/users/me').then(r => r.data),
  logoutUser: () => client.post('/users/logout'),

  // User settings / admin
  getUser: (id) => client.get(`/users/${id}`).then(r => r.data),
  listUsers: () => client.get('/users').then(r => r.data),
  createUser: (data) => client.post('/users', data).then(r => r.data),
  deleteUser: (id) => client.delete(`/users/${id}`),
  changeUserPassword: (id, newPassword) =>
    client.patch(`/users/${id}/password`, { new_password: newPassword }).then(r => r.data),
  updateUserTelegram: (id, botToken, chatId) =>
    client.patch(`/users/${id}/telegram`, { bot_token: botToken, chat_id: chatId }).then(r => r.data),
  clearUserTelegram: (id) => client.delete(`/users/${id}/telegram`).then(r => r.data),

  // Filesystem
  browseDir: (path) => client.get('/filesystem/browse', { params: path ? { path } : {} }).then(r => r.data),

  // Projects
  createProject: (data) => client.post('/projects', data).then(r => r.data),
  listProjects: () => client.get('/projects').then(r => r.data),
  getProject: (id) => client.get(`/projects/${id}`).then(r => r.data),
  updateProject: (id, data) => client.patch(`/projects/${id}`, data).then(r => r.data),
  deleteProject: (id) => client.delete(`/projects/${id}`),

  // Sessions
  createSession: (projectId, data) => client.post(`/projects/${projectId}/sessions`, data).then(r => r.data),
  listSessions: (projectId) => client.get(`/projects/${projectId}/sessions`).then(r => r.data),
  getSession: (id, opts) => client.get(`/sessions/${id}`, opts).then(r => r.data),
  pauseSession: (id) => client.post(`/sessions/${id}/pause`).then(r => r.data),
  resumeSession: (id) => client.post(`/sessions/${id}/resume`).then(r => r.data),
  cancelSession: (id) => client.post(`/sessions/${id}/cancel`).then(r => r.data),
  deleteSession: (id) => client.delete(`/sessions/${id}`),
  finalizeSession: (id) => client.post(`/sessions/${id}/finalize`).then(r => r.data),
  getFinalAnswer: (id, opts) => client.get(`/sessions/${id}/final-answer`, opts).then(r => r.data),
  reopenSession: (id, prompt, researchDepth, outputs) => client.post(`/sessions/${id}/reopen`, { prompt: prompt || null, research_depth: researchDepth || null, ...(outputs ? { outputs } : {}) }).then(r => r.data),
  // Возвращают весь ответ, а не r.data: имя файла лежит в Content-Disposition,
  // и .then(r => r.data) выбрасывал бы его вместе с остальными заголовками.
  exportFinalAnswerDocx: (id) =>
    client.get(`/sessions/${id}/export/docx`, { responseType: 'blob' }),
  exportArtifact: (artifactId, fmt) =>
    client.get(`/artifacts/${artifactId}/export/${fmt}`, { responseType: 'blob' }),
  getExportFormats: () => client.get('/export/formats').then(r => r.data),
  // Пересчёт финмодели с изменёнными допущениями: движок детерминированный,
  // языковая модель не вызывается вовсе.
  recalculateFinancialModel: (artifactId, patch) =>
    client.post(`/artifacts/${artifactId}/recalculate`, patch).then(r => r.data),

  // Tasks
  listTasks: (sessionId, opts) => client.get(`/sessions/${sessionId}/tasks`, opts).then(r => r.data),
  listAgentGoals: (sessionId, opts) => client.get(`/sessions/${sessionId}/goals`, opts).then(r => r.data),
  getTask: (id) => client.get(`/tasks/${id}`).then(r => r.data),
  retryTask: (id) => client.post(`/tasks/${id}/retry`).then(r => r.data),
  cancelTask: (id) => client.post(`/tasks/${id}/cancel`).then(r => r.data),

  // Transcript — the chat's own server-assembled projection. Deliberately not
  // derived from /artifacts: that window is shared with all agent output and
  // capped, which is what used to make old user messages disappear.
  getTranscript: (sessionId, opts) =>
    client.get(`/sessions/${sessionId}/transcript`, opts).then(r => r.data),
  sendSessionMessage: (sessionId, text, clientMsgId, attachmentIds = []) =>
    client.post(`/sessions/${sessionId}/messages`, { text, client_msg_id: clientMsgId, attachment_ids: attachmentIds }).then(r => r.data),

  // Artifacts
  listArtifacts: (sessionId, opts) => client.get(`/sessions/${sessionId}/artifacts`, opts).then(r => r.data),
  getArtifact: (id) => client.get(`/artifacts/${id}`).then(r => r.data),

  // Human Requests
  listHumanRequests: (sessionId, opts) => client.get(`/sessions/${sessionId}/human-requests`, opts).then(r => r.data),
  getHumanRequest: (id) => client.get(`/human-requests/${id}`).then(r => r.data),
  answerHumanRequest: (id, answer, clientMsgId, attachmentIds = []) =>
    client.post(`/human-requests/${id}/answer`, { answer, client_msg_id: clientMsgId, attachment_ids: attachmentIds }).then(r => r.data),

  // Trajectory — the execution ledger projection. Rows carry previews only; the
  // inspector fetches a selected record's full payload by id.
  getTrajectory: (sessionId, params) =>
    client.get(`/sessions/${sessionId}/trajectory`, { params }).then(r => r.data),

  // Decision Records
  listDecisionRecords: (sessionId, params) =>
    client.get(`/sessions/${sessionId}/decision-records`, { params }).then(r => r.data),
  getDecisionRecord: (id) => client.get(`/decision-records/${id}`).then(r => r.data),

  // Knowledge Base
  listKnowledgeBase: (projectId) => client.get(`/projects/${projectId}/knowledge`).then(r => r.data),
  ingestKnowledgeBase: (projectId, data) => client.post(`/projects/${projectId}/knowledge`, data).then(r => r.data),
  ingestKnowledgeBaseFile: (projectId, file, title) => {
    const form = new FormData();
    form.append('file', file);
    if (title) form.append('title', title);
    return client.post(`/projects/${projectId}/knowledge/upload`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },
  ingestKnowledgeBaseUrl: (projectId, url, title) =>
    client.post(`/projects/${projectId}/knowledge/from-url`, { url, title }).then(r => r.data),
  deleteKnowledgeBaseDoc: (projectId, docId) => client.delete(`/projects/${projectId}/knowledge/${docId}`),

  // Флаги наблюдателя. Только чтение и подтверждение: создать флаг извне
  // нельзя — тогда он был бы неотличим от поднятого монитором, а вся
  // ценность ленты в том, что человек знает её источник.
  listSessionFlags: (sessionId) => client.get(`/sessions/${sessionId}/flags`).then(r => r.data),
  acknowledgeSessionFlag: (sessionId, flagId) =>
    client.post(`/sessions/${sessionId}/flags/${flagId}/acknowledge`),

  // Agents as data (AgentSpec rows). `reload` не толкает воркеров: у
  // prefork-воркера N процессов, и рассылка дошла бы до одного, а остальные
  // продолжили бы отдавать старые промпты. Каждый процесс перечитывает сам
  // перед задачей, а этот вызов валидирует строки и показывает состав.
  getAgentRoster: () => client.get('/agents').then(r => r.data),
  reloadAgents: () => client.post('/agents/reload').then(r => r.data),
  listAgentSpecs: () => client.get('/agents/specs').then(r => r.data),
  getAgentSpec: (slug) => client.get(`/agents/specs/${slug}`).then(r => r.data),
  createAgentSpec: (spec, revision) => client.post('/agents/specs', spec, { params: { base_bundle_revision: revision } }).then(r => r.data),
  updateAgentSpec: (slug, data) => client.put(`/agents/specs/${slug}`, data).then(r => r.data),
  forkAgentSpec: (slug, data) => client.post(`/agents/specs/${slug}/fork`, data || {}).then(r => r.data),
  archiveAgentSpec: (slug, revision) => client.delete(`/agents/specs/${slug}`, { params: { base_bundle_revision: revision } }),

  // Administrator-managed model and ACP connections. Secret values are write-only.
  listProviders: () => client.get('/admin/providers').then(r => r.data),
  createProvider: data => client.post('/admin/providers', data).then(r => r.data),
  updateProvider: (id, data) => client.put(`/admin/providers/${id}`, data).then(r => r.data),
  clearProviderSecret: id => client.delete(`/admin/providers/${id}/secret`),
  getProviderModels: id => client.get(`/admin/providers/${id}/models`).then(r => r.data),
  listConnections: () => client.get('/admin/connections').then(r => r.data),
  createConnection: (data) => client.post('/admin/connections', data).then(r => r.data),
  updateConnection: (id, data) => client.put(`/admin/connections/${id}`, data).then(r => r.data),
  clearConnectionSecret: (id) => client.delete(`/admin/connections/${id}/secret`),
  checkConnection: (id) => client.post(`/admin/connections/${id}/check`).then(r => r.data),
  assignConnection: (target, connectionId) => client.put(
    `/admin/connections/assignments/${encodeURIComponent(target)}`,
    { connection_id: connectionId || null },
  ).then(r => r.data),
  startConnectionAuth: (id) => client.post(`/admin/connections/${id}/auth/start`).then(r => r.data),
  getConnectionAuth: (id, attemptId) => client.get(
    `/admin/connections/${id}/auth/${attemptId}`,
  ).then(r => r.data),
  sendConnectionAuthInput: (id, attemptId, value) => client.post(
    `/admin/connections/${id}/auth/${attemptId}/input`, { value },
  ).then(r => r.data),
  cancelConnectionAuth: (id, attemptId) => client.delete(
    `/admin/connections/${id}/auth/${attemptId}`,
  ),

  // Skills (R&D methodologies stored as data)
  listSkills: (params) => client.get('/skills', { params }).then(r => r.data),
  getSkill: (slug) => client.get(`/skills/${slug}`).then(r => r.data),
  listSkillVersions: (slug) => client.get(`/skills/${slug}/versions`).then(r => r.data),
  createSkill: (data) => client.post('/skills', data).then(r => r.data),
  updateSkill: (slug, data) => client.put(`/skills/${slug}`, data).then(r => r.data),
  forkSkill: (slug, data) => client.post(`/skills/${slug}/fork`, data).then(r => r.data),
  archiveSkill: (slug) => client.delete(`/skills/${slug}`),
  validateSkill: (spec) => client.post('/skills/validate', spec).then(r => r.data),
  listSessionSkills: (sessionId) => client.get(`/sessions/${sessionId}/skills`).then(r => r.data),

  // Platform observability (admin-only). Экспорт возвращает ВЕСЬ ответ, а не
  // r.data — как и другие blob-скачивания выше, имя файла едет в
  // Content-Disposition и `.then(r => r.data)` выбросил бы его вместе с
  // остальными заголовками.
  getPlatformMetrics: (params) => client.get('/admin/metrics', { params }).then(r => r.data),
  getModelUsage: params => client.get('/admin/model-usage', { params }).then(r => r.data),
  getModelUsageCalls: params => client.get('/admin/model-usage/calls', { params }).then(r => r.data),
  exportModelUsage: params => client.get('/admin/model-usage/export.csv', { params, responseType: 'blob' }),
  getLogs: params => client.get('/admin/logs', { params }).then(r => r.data),
  listTokenQuotas: () => client.get('/admin/token-quotas').then(r => r.data),
  getMyTokenQuota: () => client.get('/users/me/token-quota').then(r => r.data),
  setTokenQuota: (id, limit) => client.put(`/admin/users/${id}/token-quota`, { limit }).then(r => r.data),
  resetTokenQuota: (id, requestId) => client.post(`/admin/users/${id}/token-quota/reset`, { request_id: requestId }).then(r => r.data),
  exportPlatformObservability: (params) =>
    client.get('/admin/observability/export', { params, responseType: 'blob' }),
  exportSessionObservability: (sessionId) =>
    client.get(`/sessions/${sessionId}/observability/export`, { responseType: 'blob' }),
};

export default client;

export const apiError = error => {
  const body = error?.response?.data;
  return body?.error?.message || (typeof body?.detail === 'string' ? body.detail : 'Не удалось выполнить действие. Повторите попытку.');
};

// Refresh the shared action queue only after a successful state mutation.
client.interceptors.response.use(response => {
  if (['post', 'put', 'patch', 'delete'].includes(response.config?.method?.toLowerCase()))
    window.dispatchEvent(new Event('attention:invalidate'));
  return response;
});
